import { administration, metadata, serverMessageOf } from './support'

type AuthOpt =
  | { token?: string }
  | { Authorization?: string; authorization?: string }
  | undefined

export type MaybeHttpError = {
  httpStatus?: number
  status?: number
  statusCode?: number
  response?: { status?: number }
  body?: { detail?: string; message?: string; Title?: string }
  message?: string
}

export const httpStatusOf = (err: unknown): number | undefined => {
  const e = err as MaybeHttpError
  return e?.httpStatus ?? e?.status ?? e?.response?.status ?? e?.statusCode
}

export const messageOf = (err: unknown): string | undefined => {
  const e = err as MaybeHttpError
  return serverMessageOf(e?.body) || e?.message
}

const makeHeaders = (opt: AuthOpt) => {
  if (!opt) return undefined
  const h: Record<string, string> = {}
  if ('token' in opt && opt?.token) h.Authorization = `Bearer ${opt.token}`
  if ('Authorization' in (opt || {}) && (opt as any).Authorization)
    h.Authorization = (opt as any).Authorization
  if ('authorization' in (opt || {}) && (opt as any).authorization)
    h.Authorization = (opt as any).authorization
  return Object.keys(h).length ? h : undefined
}

const toItems = (resp: any) =>
  Array.isArray(resp) ? resp : resp?.data ?? resp?.items ?? []

export type Project = { id: string; name: string }
export type Folder = { id: string; name: string }

export const getUserTenants = () => administration.getJson('iam/tenant/my', 3)

export const listProjects = (opt?: AuthOpt) =>
  metadata.getJson(
    'metadata/project/offset-list?offset=0&limit=100',
    3,
    makeHeaders(opt)
  )

export const listFolders = (projectId: string, opt?: AuthOpt) =>
  metadata.getJson(
    `metadata/project/${projectId}/subproject/offset-list?offset=0&limit=100&sortBy=UpdatedAt&sortOrder=Desc`,
    3,
    makeHeaders(opt)
  )

export { listFolders as listSubprojects }

export const listFoldersRecursive = (projectId: string, opt?: AuthOpt) =>
  metadata.getJson(
    `metadata/project/${projectId}/offset-list-recursive?Offset=0&Limit=1000`,
    3,
    makeHeaders(opt)
  )

export const probeOneProject = async (
  opt?: AuthOpt
): Promise<Project | null> => {
  const resp = await metadata.getJson(
    'metadata/project/offset-list?offset=0&limit=1',
    3,
    makeHeaders(opt)
  )
  const items = toItems(resp) as Project[]
  return items[0] || null
}

let preferPrincipalOffsetList = false

export const listPrincipalsForProject = async (
  projectId: string,
  opt?: AuthOpt
) => {
  const hdr = makeHeaders(opt)
  if (!preferPrincipalOffsetList) {
    try {
      return await administration.getJson(
        `iam/project/${encodeURIComponent(projectId)}/principal/list`,
        3,
        hdr
      )
    } catch (e) {
      const status = httpStatusOf(e)
      if (status === 400 || status === 404 || status === 405) {
        preferPrincipalOffsetList = true
      } else {
        const err = new Error(messageOf(e) || 'Failed to list principals')
        ;(err as any).status = status
        throw err
      }
    }
  }
  return administration.getJson(
    `iam/project/${encodeURIComponent(
      projectId
    )}/principal/offset-list?offset=0&limit=200`,
    3,
    hdr
  )
}

export const moveDataset = (
  datasetId: string,
  targetTenantId: string,
  targetProjectId: string
) =>
  metadata.postJson(
    `metadata/dataset/${datasetId}/move`,
    { targetTenantId, targetProjectId },
    4
  )

export const copyDataset = (
  datasetId: string,
  targetTenantId: string,
  targetProjectId: string
) =>
  metadata.postJson(
    `metadata/dataset/${datasetId}/copy`,
    { targetTenantId, targetProjectId },
    4
  )

export type TransferOutput = {
  id: string
  status: string
  format?: string
  errorMessage?: string
  [k: string]: any
}

export const getTransferStatus = (id: string, opt?: AuthOpt) =>
  metadata.getJson(
    `conversion/transfer/${encodeURIComponent(id)}`,
    2,
    makeHeaders(opt)
  )
